import Vue from 'vue';
import Vuex, {ActionTree, GetterTree, MutationTree} from 'vuex';

import {ResultsState} from '../models/store-viewmodel';

import {Config} from '../models/config-viewmodel';
import {Result} from '../models/result-viewmodel';
import {IResult} from '../models/result-model';

import ResultMapper from '../mappings/result-mapper';

import {Query, VUwebSearchService} from '@/shared/services/azure-search-service';
import {currentDate} from '@/shared/services/format-date-filter';

Vue.use(Vuex);

export const storeState: ResultsState = {
    results: [],
    totalResults: 0,
    resultsPerPage: 0,
    currentPage: 0,
    loading: true,
    errors: [],
    azureService: new VUwebSearchService(''),
    config: {
        language: '',
        apiBasePath: '',
        overviewUrl: '',
        labels: {},
        searchIndex: '',
        searchEndpoint: '',
        searchFilters: [],
        isOverview: false,
        resultType: '',
        resultsPerPage: 0,
        imageFallback: ''
    }
}

export const mutations: MutationTree<ResultsState> = {
    setConfig(state, config: Config) {
        state.config = config;
    },

    setLoading(state, loading: boolean) {
        state.loading = loading;
    },

    setResults(state, results: Result[]) {
        state.results = results;
    },

    setTotalResults(state, total: number) {
        state.totalResults = total;
    },

    setResultsPerPage(state, resultsPerPage: number) {
        state.resultsPerPage = resultsPerPage;
    },

    setCurrentPage(state, page: number) {
        state.currentPage = page;
    },

    setAzureSearch(state) {
        state.azureService = new VUwebSearchService(state.config.searchIndex);
    },

    setErrors(state, errors: string[]) {
        state.errors = errors;
    }
}

export const actions: ActionTree<ResultsState, ResultsState> = {
    getResults({state}, [query, onSucces, onFailure]) {
        if (state.azureService) {
            state.azureService.search(query)
                .then((res: any) => {
                    onSucces(res)
                }, (err: any) => {
                    onFailure(err);
                });
        }
    },

    loadMore({getters, commit, dispatch}) {
        if (getters.notLastResult && !getters.loading) {
            commit("setCurrentPage", getters.currentPage + 1);
            commit("setLoading", true);
            dispatch('loadData', [true]);
        }
    },

    loadAll({getters, commit, dispatch}) {
        if (getters.notLastResult && !getters.loading) {
            commit("setCurrentPage", getters.currentPage + 1);
            commit("setLoading", true);
            dispatch('loadData', [true, true]).then(() => {
                commit("setResultsPerPage", getters.totalResults);
            });
        }
    },

    loadData({commit, getters, dispatch}, [append, all]) {
        dispatch('getResults', [getters.query(all), (results: any) => {
            let resultMapper = new ResultMapper();
            let resultsMapped = results.value.map((result: IResult) => resultMapper.mapResult(result));

            if (append) {
                resultsMapped = [...getters.results, ...resultsMapped];
            }

            commit('setResults', resultsMapped);
            commit('setTotalResults', results['@odata.count'])
            commit("setLoading", false);
        }, (error: any) => {
            console.error(error);
            commit('setErrors', [getters.getErrorText])
            commit("setLoading", false);
        }]);
    },
}

export const getters: GetterTree<ResultsState, ResultsState> = {
    config(state) {
        return state.config;
    },

    notLastResult(state) {
        return state.resultsPerPage * (state.currentPage + 1) < state.totalResults;
    },

    skipResults(state) {
        return state.currentPage * state.resultsPerPage
    },

    results(state) {
        return state.results;
    },

    loading(state) {
        return state.loading;
    },

    currentPage(state) {
        return state.currentPage;
    },

    resultsPerPage(state) {
        return state.resultsPerPage;
    },

    totalResults(state) {
        return state.totalResults;
    },

    query: (state, getters) => (all: boolean = false) => {
        return new Query()
            .SetCount(true)
            .SetSkip(getters.skipResults)
            .SetTop(all ? getters.totalResults : getters.resultsPerPage)
            .SetFilter("ItemType/any(c: c eq '" + getters.config.resultType + "')")
            .AddFilter("Language eq '" + getters.config.language.toUpperCase() + "'")
            .AddFilter(getters.config.searchFilters ? getters.searchFilters : '')
            .AddFilter(getters.config.extraFilter ? getters.config.extraFilter.replace('{currentDate}', currentDate()) : '')
            .SetScoringProfile('BoostTitle')
            .SetOrderBy(getters.config.resultsOrder);
    },

    getErrorText(state, getters) {
        return getters.config.language.toLowerCase() === 'nl'
            ? "Er gaat iets fout, probeer het later nog een keer"
            : "Something went wrong, please try again later";
    },

    errors(state) {
        return state.errors;
    },

    searchFilters(state, getters): string {
        let newArr = getters.config.searchFilters.map((filter: { tags: [], generics: [] }) => {
            let filterArr: string[] = [];
            if (filter.tags.length > 0) {
                let subFilterTagsArr = filter.tags.map((a: string) => {
                    return `Filter/any(c: search.in(c, '${a}'))`;
                });

                filterArr.push(`(${subFilterTagsArr.join(' and ')})`);
            }

            if (filter.generics.length > 0) {
                let subFilterGenericsArr = filter.generics.map((a: string) => {
                    return `GenericFilter/any(c: search.in(c, '${a}'))`;
                });

                filterArr.push(`(${subFilterGenericsArr.join(' and ')})`);
            }

            return filterArr.length > 0 ? `(${filterArr.join(' and ')})` : null;
        });

        return newArr.filter(Boolean).length > 0 ? `(${newArr.join(' or ')})` : '';
    }
}
